import React, { useMemo } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core'
import { ArrowBack as ArrowBackIcon } from '@material-ui/icons'
import {
    MetricDefinition,
    PROJECT_METRICS,
    PROJECT_METRIC_GROUPS,
    ProjectSnapshotDetail,
    ProjectSnapshotKpis,
    formatMetricValue,
} from './auditMetrics'

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5),
        width: '100%',
    },
    toolbar: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    toolbarLeft: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        minWidth: 0,
    },
    backButton: {
        textTransform: 'none',
        paddingLeft: 0,
        minWidth: 0,
        color: theme.palette.text.secondary,
        '&:hover': {
            backgroundColor: 'transparent',
            color: theme.palette.primary.main,
        },
    },
    title: {
        fontWeight: 600,
    },
    subtitle: {
        color: theme.palette.text.secondary,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5),
    },
    groupsStack: {
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
    },
    groupStrip: {
        display: 'flex',
        alignItems: 'stretch',
        gap: 0,
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
        },
    },
    groupLabelRail: {
        flex: '0 0 132px',
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1.25, 1.5),
        backgroundColor: theme.palette.grey[50],
        borderRight: `2px solid ${theme.palette.grey[700]}`,
        [theme.breakpoints.down('xs')]: {
            flex: 'none',
            borderRight: 'none',
            borderBottom: `2px solid ${theme.palette.grey[700]}`,
            paddingBottom: theme.spacing(0.75),
        },
    },
    groupTitle: {
        fontWeight: 700,
        fontSize: '0.7rem',
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        color: theme.palette.text.secondary,
        lineHeight: 1.3,
    },
    metricStrip: {
        flex: 1,
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'stretch',
        minWidth: 0,
        overflowX: 'auto',
    },
    metricCell: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 2,
        padding: theme.spacing(1.25, 1.5),
        width: 140,
        minWidth: 140,
        maxWidth: 140,
        boxSizing: 'border-box',
        borderRight: `1px solid ${theme.palette.divider}`,
    },
    metricCellCaiPct: {
        border: `2px solid ${theme.palette.primary.main}`,
        // Keep alignment with neighboring cells that only have a right divider
        marginTop: -1,
        marginBottom: -1,
        zIndex: 1,
        position: 'relative',
        backgroundColor: theme.palette.background.paper,
    },
    kpiLabel: {
        fontSize: '0.65rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    kpiValue: {
        fontWeight: 700,
        fontSize: '1.05rem',
        lineHeight: 1.15,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    warnValue: {
        color: theme.palette.warning.dark,
    },
    dangerValue: {
        color: theme.palette.error.main,
    },
    sectionTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(1.5),
    },
    behindCell: {
        color: theme.palette.error.main,
        fontWeight: 600,
    },
    empty: {
        padding: theme.spacing(4),
        textAlign: 'center',
    },
    tableWrap: {
        width: '100%',
        overflowX: 'auto',
    },
}))

interface Props {
    onBack: () => void
    detail: ProjectSnapshotDetail | null
    /** Metrics from the comparison-grid row (source of truth for KPI cards). */
    metrics?: Record<string, number | null> | null
    isLoading?: boolean
    errorMessage?: string | null
}

const metricByKey = PROJECT_METRICS.reduce<Record<string, MetricDefinition>>(
    (acc, metric) => {
        acc[metric.key] = metric
        return acc
    },
    {},
)

const formatCurrencyOrDash = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
        return '—'
    }
    return formatMetricValue(value, 'currency')
}

const getMetricHint = (
    key: string,
    metrics: Record<string, number | null | undefined>,
    detailKpis?: ProjectSnapshotKpis | null,
): string | null => {
    switch (key) {
        case 'cai_pct':
            return `${formatMetricValue(
                metrics.cai,
                'number',
            )} CAI / ${formatMetricValue(
                metrics.total_workorders,
                'number',
            )} total`
        case 'behind_pct':
            return `${formatMetricValue(
                metrics.total_behind,
                'number',
            )} work orders behind`
        case 'units_ready_pct': {
            const ready = detailKpis?.units_ready_count
            const scope = detailKpis?.units_ready_scope_count
            if (ready != null && scope != null) {
                return `${formatMetricValue(
                    ready,
                    'number',
                )} / ${formatMetricValue(scope, 'number')} in scope`
            }
            return null
        }
        case 'total_budget':
            return `Forecast ${formatMetricValue(
                metrics.total_forecasted,
                'currency',
            )}`
        case 'total_forecasted':
            return `Budget ${formatMetricValue(
                metrics.total_budget,
                'currency',
            )}`
        case 'budget_variance': {
            const variance = metrics.budget_variance
            if (variance == null) {
                return null
            }
            return variance < 0 ? 'Over budget' : 'Under / on budget'
        }
        case 'avg_forecasted_per_project':
            return `${formatMetricValue(
                metrics.total_projects,
                'number',
            )} projects`
        case 'total_workorders':
            return `${formatMetricValue(
                metrics.total_projects,
                'number',
            )} projects`
        default:
            return null
    }
}

const getValueClass = (
    key: string,
    value: number | null | undefined,
    classes: ReturnType<typeof useStyles>,
): string => {
    if (value == null) {
        return classes.kpiValue
    }
    if (key === 'behind_pct' || key === 'total_behind') {
        return `${classes.kpiValue} ${classes.warnValue}`
    }
    if (key === 'budget_variance' && value < 0) {
        return `${classes.kpiValue} ${classes.dangerValue}`
    }
    return classes.kpiValue
}

export const ProjectSnapshotDetailView: React.FC<Props> = ({
    onBack,
    detail,
    metrics: rowMetrics = null,
    isLoading = false,
    errorMessage = null,
}) => {
    const classes = useStyles()

    const metrics = useMemo(() => {
        const fromRow = rowMetrics ?? {}
        const fromDetail = detail?.detail?.kpis ?? {}
        return {
            ...fromDetail,
            ...fromRow,
        } as Record<string, number | null | undefined>
    }, [rowMetrics, detail])

    const services = detail?.detail?.services ?? []
    const detailKpis = detail?.detail?.kpis
    const hasMetrics = Object.keys(metrics).length > 0
    const reportLabel = detail?.label

    return (
        <Box className={classes.root}>
            <Box className={classes.toolbar}>
                <Box className={classes.toolbarLeft}>
                    <Button
                        className={classes.backButton}
                        startIcon={<ArrowBackIcon />}
                        onClick={onBack}
                        size="small"
                    >
                        Back to table
                    </Button>
                    <Box>
                        <Typography variant="h6" className={classes.title}>
                            Project Report
                            {reportLabel ? ` · ${reportLabel}` : ''}
                        </Typography>
                        <Typography
                            variant="body2"
                            className={classes.subtitle}
                        >
                            Snapshot detail for this report period. Property
                            filters above still apply.
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box className={classes.content}>
                {errorMessage && !hasMetrics && (
                    <Typography color="error">{errorMessage}</Typography>
                )}

                {hasMetrics && (
                    <Box className={classes.groupsStack}>
                        {PROJECT_METRIC_GROUPS.map((group) => (
                            <Box key={group.id} className={classes.groupStrip}>
                                <Box className={classes.groupLabelRail}>
                                    <Typography className={classes.groupTitle}>
                                        {group.label}
                                    </Typography>
                                </Box>
                                <Box className={classes.metricStrip}>
                                    {group.metricKeys.map((key) => {
                                        const definition = metricByKey[key]
                                        if (!definition) {
                                            return null
                                        }
                                        const value = metrics[key]
                                        const hint = getMetricHint(
                                            key,
                                            metrics,
                                            detailKpis,
                                        )
                                        return (
                                            <Box
                                                key={key}
                                                className={`${
                                                    classes.metricCell
                                                }${
                                                    key === 'cai_pct'
                                                        ? ` ${classes.metricCellCaiPct}`
                                                        : ''
                                                }`}
                                                title={hint || undefined}
                                            >
                                                <Typography
                                                    className={classes.kpiLabel}
                                                >
                                                    {definition.label}
                                                </Typography>
                                                <Typography
                                                    className={getValueClass(
                                                        key,
                                                        value,
                                                        classes,
                                                    )}
                                                >
                                                    {formatMetricValue(
                                                        value,
                                                        definition.format,
                                                    )}
                                                </Typography>
                                            </Box>
                                        )
                                    })}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}

                <Box>
                    <Typography className={classes.sectionTitle}>
                        Services Breakdown
                    </Typography>
                    {isLoading && !detail ? (
                        <Box className={classes.empty}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : errorMessage && !detail ? (
                        <Typography color="error">{errorMessage}</Typography>
                    ) : (
                        <>
                            {services.length === 0 ? (
                                <Typography color="textSecondary">
                                    No services breakdown was stored for this
                                    snapshot. Generate a new project report to
                                    capture it.
                                </Typography>
                            ) : (
                                <Box className={classes.tableWrap}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    Service Category
                                                </TableCell>
                                                <TableCell align="right">
                                                    # Line Items
                                                </TableCell>
                                                <TableCell align="right">
                                                    Total Workorders
                                                </TableCell>
                                                <TableCell align="right">
                                                    Complete
                                                </TableCell>
                                                <TableCell align="right">
                                                    Behind
                                                </TableCell>
                                                <TableCell align="right">
                                                    Complete %
                                                </TableCell>
                                                <TableCell align="right">
                                                    Total Budget
                                                </TableCell>
                                                <TableCell align="right">
                                                    Forecasted Cost
                                                </TableCell>
                                                <TableCell align="right">
                                                    Cost / Unit
                                                </TableCell>
                                                <TableCell align="right">
                                                    Cost / Bed
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {services.map((row) => (
                                                <TableRow key={row.category}>
                                                    <TableCell>
                                                        {row.category}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatMetricValue(
                                                            row.line_items,
                                                            'number',
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatMetricValue(
                                                            row.total_workorders,
                                                            'number',
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatMetricValue(
                                                            row.complete,
                                                            'number',
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        className={
                                                            row.behind > 0
                                                                ? classes.behindCell
                                                                : undefined
                                                        }
                                                    >
                                                        {formatMetricValue(
                                                            row.behind,
                                                            'number',
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatMetricValue(
                                                            row.complete_pct,
                                                            'percent',
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrencyOrDash(
                                                            row.total_budget,
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrencyOrDash(
                                                            row.forecasted_cost,
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrencyOrDash(
                                                            row.cost_per_unit,
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrencyOrDash(
                                                            row.cost_per_bed,
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

/** @deprecated Use ProjectSnapshotDetailView */
export const ProjectSnapshotDetailDialog = ProjectSnapshotDetailView
